import { ServiceInformation } from "@/src/pages/admin/components/PatchesAndInfoManagement";

import { api, apiPaths } from "../../api";
import {
  AuctionFlagDTO,
  AuctionQuestionDTO,
  AuctionsSummary,
  BidDTO,
  CreateAuctionFlagRequestBody,
  CreateBidBody,
  CreateBidMachineBody,
  CreateNegotiationMessageBody,
  CreateNoteRequestBody,
  DealerAuctionDTO,
  FinalOfferResponse,
  ListAuctionsFilterParam,
  NegotiationMessageDTO,
  NoteDTO,
  PreviousAuctionNoteDTO,
  UpdateBidBody,
  UpdateUserContactInfo,
  UpdateUserRequestBody,
  UserDTO,
  UserPreferences,
  WithdrawOfferRequestBody
} from "../../types/types";

export enum DealerQueryKeys {
  AUCTION = "dealer/auction",
  AUCTIONS = "dealer/auctions",
  ENDED_AUCTIONS = "dealer/endedAuctions",
  MY_ACTIVE_AUCTIONS = "dealer/myActive",
  AUCTIONS_SUMMARY = "dealer/auctionsSummary",
  NOTE_HISTORY = "dealer/noteHistory"
}

export const getAuctionItem = async (
  auctionId: number
): Promise<DealerAuctionDTO | null> => {
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}`)
    .get()
    .json()
    .then((res) => {
      return res as DealerAuctionDTO;
    })
    .catch((error) => {
      console.error(`getAuctionItem failed for ID ${auctionId}:`, {
        status: error.status,
        message: error.message,
        response: error.response
      });

      // Return null for 404 (auction doesn't exist) and 403 (auction deleted, no longer accessible)
      // For other errors (network, server), throw the error so retry logic can handle it
      if (error.status === 404 || error.status === 403) {
        console.warn(
          `Auction ${auctionId} not found or no longer accessible (${error.status})`
        );
        return null;
      }

      // For non-404 errors, throw the error so React Query can retry
      console.error(
        `Non-404 error for auction ${auctionId}, throwing for retry logic`
      );
      throw error;
    });
};

export const getActiveAuctions = async (): Promise<DealerAuctionDTO[]> => {
  return api
    .url(apiPaths.AUCTIONS)
    .get(`?filter=${ListAuctionsFilterParam.Active}`)
    .json()
    .then((res) => {
      return res as DealerAuctionDTO[];
    })
    .catch((error) => {
      console.error("getActiveAuctions failed:", {
        status: error?.status,
        message: error?.message,
        response: error?.response
      });
      // Return empty array to prevent crashes
      return [];
    });
};

interface GetEndedAuctionsParams {
  searchTerm?: string;
  limit?: number;
  skip?: number;
  filter: string;
  sortDirection?: string;
  auctionScope?: string;
}

export const getEndedAuctions = async ({
  searchTerm,
  skip = 0,
  limit = 50,
  filter,
  sortDirection,
  auctionScope
}: GetEndedAuctionsParams): Promise<DealerAuctionDTO[]> => {
  return api
    .url(apiPaths.AUCTIONS)
    .get(
      `?${filter}
${searchTerm ? `&searchTerm=${searchTerm}` : ""}
&skip=${skip}
&limit=${limit}
${sortDirection ? `&sortDirection=${sortDirection}` : ""}${
        auctionScope ? `&auctionScope=${auctionScope}` : ""
      }
`
    )
    .json()
    .then((res) => {
      return res as DealerAuctionDTO[];
    })
    .catch(() => {
      return [];
    });
};

// Create a new manual bid or manual bid + bid machine.
export const postBid = async (props: {
  data: CreateBidBody;
  auctionId: number;
}): Promise<BidDTO | Error> => {
  return api
    .url(`${apiPaths.AUCTIONS}/${props.auctionId}/bids`)
    .post({
      ...props.data
    } as CreateBidBody)
    .json()
    .then((res) => {
      return res as BidDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const acceptBidForAuction = async (props: {
  auctionId: number;
  bidId: number;
}): Promise<DealerAuctionDTO | Error> => {
  const { bidId, auctionId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/offers/${bidId}/accept`)
    .put()
    .json()
    .then((res) => {
      return res as DealerAuctionDTO;
    })
    .catch((error) => {
      throw error;
    });
};

export const putNewOfferToBid = async (props: {
  auctionId: number;
  bidId: number;
  amount: number;
  updateType: "BID_UPDATED";
}): Promise<BidDTO | Error> => {
  return api
    .url(`${apiPaths.AUCTIONS}/${props.auctionId}/bids/${props.bidId}`)
    .put({
      amount: props.amount,
      updateType: props.updateType
    })
    .json()
    .then((res) => {
      return res as BidDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const submitFinalOffer = async (props: {
  auctionId: number;
  bidId: number;
  amount: number;
}): Promise<FinalOfferResponse | Error> => {
  return api
    .url(
      `${apiPaths.AUCTIONS}/${props.auctionId}/bids/${props.bidId}/final-offer`
    )
    .post({
      amount: props.amount
    })
    .json()
    .then((res) => {
      return res as FinalOfferResponse;
    })
    .catch((e) => {
      throw e;
    });
};

export const changeStateAndOffer = async (props: {
  auctionId: number;
  bidId: number;
  amount: number;
  state: string;
  updateReason?: string;
  updateType: "BID_UPDATED_AND_STATE_CHANGE";
}): Promise<UpdateBidBody | Error> => {
  return api
    .url(`${apiPaths.AUCTIONS}/${props.auctionId}/bids/${props.bidId}`)
    .put({
      amount: props.amount,
      auctionState: props.state,
      updateReason: props.updateReason,
      updateType: props.updateType
    })
    .json()
    .then((res) => {
      return res as UpdateBidBody;
    })
    .catch((e) => {
      throw e;
    });
};

// Create a new bid machine and attach it to the bid. BidId must be provided.
export const postBidMachine = async (props: {
  data: CreateBidMachineBody;
  auctionId: number;
}): Promise<null | Error> => {
  return api
    .url(`${apiPaths.AUCTIONS}/${props.auctionId}/bidMachines`)
    .post({
      ...props.data
    } as CreateBidMachineBody)
    .json()
    .then(() => {
      return null;
    })
    .catch((e) => {
      throw e;
    });
};

// Modify an existing bid machine.
export const updateBidMachine = async (props: {
  auctionId: number;
  bidMachineId: number;
  isActive?: boolean;
  maxAmount?: number;
  guaranteedAmount?: number | null;
  pickupIncluded?: boolean;
}): Promise<null | Error> => {
  const data = {};
  if (props.isActive !== undefined) {
    data["isActive"] = props.isActive;
  }
  if (props.maxAmount !== undefined) {
    data["maxAmount"] = props.maxAmount;
  }
  if (props.guaranteedAmount !== undefined) {
    data["guaranteedAmount"] = props.guaranteedAmount;
  }
  if (props.pickupIncluded !== undefined) {
    data["pickupIncluded"] = props.pickupIncluded;
  }
  return api
    .url(
      `${apiPaths.AUCTIONS}/${props.auctionId}/bidMachines/${props.bidMachineId}`
    )
    .put(data)
    .json()
    .then(() => {
      return null;
    })
    .catch((e) => {
      throw e;
    });
};

export const postNegotiationMessage = async (props: {
  message: CreateNegotiationMessageBody;
  auctionId: number;
}): Promise<NegotiationMessageDTO | Error> => {
  const { message, auctionId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/negotiations`)
    .post({
      ...message
    } as CreateNegotiationMessageBody)
    .json()
    .then((res) => {
      return res as NegotiationMessageDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const markNegotiationMessagesAsRead = async (bidId: number) => {
  return api
    .url(`${apiPaths.AUCTIONS}/negotiations/markAsRead/${bidId}`)
    .put()
    .json()
    .then((res) => {
      return res;
    })
    .catch((e) => {
      throw e;
    });
};
export const putWithdrawOffer = async (props: {
  message: WithdrawOfferRequestBody;
  auctionId: number;
  bidId: number;
}): Promise<DealerAuctionDTO | Error> => {
  const { message, auctionId, bidId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/offers/${bidId}/withdraw`)
    .put({
      ...message
    } as WithdrawOfferRequestBody)
    .json()
    .then((res) => {
      return res as DealerAuctionDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const putSetSold = async (props: {
  auctionId: number;
  bidId: number;
}): Promise<DealerAuctionDTO | Error> => {
  const { auctionId, bidId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/offers/${bidId}/setSold`)
    .put({})
    .json()
    .then((res) => {
      return res as DealerAuctionDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const fetchAuctionItem = (
  auctionId: number,
  onLoaded: (DealerAuctionDTO) => void
) => {
  getAuctionItem(auctionId)
    .then((auctionItem) => {
      if (auctionItem) {
        console.log(
          `Successfully fetched auction ${auctionId} for IoT processing`
        );
        onLoaded(auctionItem);
      } else {
        console.warn(
          `Auction ${auctionId} not found or invalid - skipping IoT message processing`
        );
        // Don't call onLoaded callback when auction item is invalid
        // This prevents the IoT system from invalidating all queries for invalid auctions
      }
    })
    .catch((error) => {
      console.error(
        `Failed to fetch auction item ${auctionId} for IoT processing:`,
        {
          status: error?.status,
          message: error?.message,
          error: error
        }
      );
      // Don't call onLoaded callback on API errors either
      // This prevents IoT system from processing with invalid data
    });
};

// For Notes
export const postNote = async (props: {
  content: CreateNoteRequestBody;
  auctionId: number;
}): Promise<NoteDTO | Error> => {
  const { content, auctionId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/notes`)
    .post({
      ...content
    } as CreateNoteRequestBody)
    .json()
    .then((res) => {
      return res as NoteDTO;
    })
    .catch((e) => {
      throw e;
    });
};

// For Auction flags (dealer reports of an auction to admins)
export const postAuctionFlag = async (props: {
  content: CreateAuctionFlagRequestBody;
  auctionId: number;
}): Promise<AuctionFlagDTO | Error> => {
  const { content, auctionId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/flags`)
    .post({
      ...content
    } as CreateAuctionFlagRequestBody)
    .json()
    .then((res) => {
      return res as AuctionFlagDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const putUserSettings = async (
  contactInfo: UpdateUserContactInfo,
  preferences: UserPreferences
) => {
  return api
    .url(`${apiPaths.SETTINGS}`)
    .put({
      contactInfo: contactInfo,
      preferences: preferences
    } as UpdateUserRequestBody)
    .json()
    .then((res) => {
      return res as UserDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const putNote = async (props: {
  content: CreateNoteRequestBody;
  auctionId: number;
  noteId: number;
}): Promise<NoteDTO | Error> => {
  const { auctionId, noteId, content } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/notes/${noteId}`)
    .put({
      ...content
    } as CreateNoteRequestBody)
    .json()
    .then((res) => {
      return res as NoteDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const deleteNote = async (props: {
  auctionId: number;
  noteId: number;
}): Promise<NoteDTO | Error> => {
  const { auctionId, noteId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/notes/${noteId}`)
    .delete()
    .json()
    .then((res) => {
      return res as NoteDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const getNoteHistory = async (
  auctionId: number
): Promise<PreviousAuctionNoteDTO[]> => {
  const res = await api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/notes/history`)
    .get()
    .json();
  return res as PreviousAuctionNoteDTO[];
};

// For Questions
export const postQuestion = async (props: {
  content: string;
  auctionId: number;
}): Promise<AuctionQuestionDTO | Error> => {
  const { content, auctionId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/questions`)
    .post({
      question: content
    })
    .json()
    .then((res) => {
      return res as AuctionQuestionDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const deleteQuestion = async (props: {
  auctionId: number;
  questionId: number;
}): Promise<AuctionQuestionDTO | Error> => {
  const { auctionId, questionId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/questions/${questionId}`)
    .delete()
    .json()
    .then((res) => {
      return res as AuctionQuestionDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const toggleFavourite = async (props: { auctionId: number }) => {
  const { auctionId } = props;
  return api
    .url(`${apiPaths.AUCTIONS}/${auctionId}/toggleFavourite`)
    .post()
    .json()
    .then((res) => {
      return res as DealerAuctionDTO;
    })
    .catch((e) => {
      throw e;
    });
};

export const getAuctionsSummary = async () => {
  return api
    .url(`${apiPaths.AUCTIONS_SUMMARY}`)
    .get()
    .json()
    .then((res) => res as AuctionsSummary);
};

export const getDealerPatchesAndInfo = async () => {
  return api
    .url(`${apiPaths.PATCH_AND_INFO}`)
    .get()
    .json()
    .then((res) => res as ServiceInformation)
    .catch((error) => {
      console.log("Received error", error);
    });
};

export const toggleHide = async (props: { auctionId: number }) => {
  const { auctionId } = props;
  return api
    .url(`${apiPaths.USER_ACTIONS}/hide/${auctionId}`)
    .post()
    .json()
    .then((res) => {
      return res as { message: string };
    })
    .catch((e) => {
      throw e;
    });
};

export const toggleFavouriteAction = async (props: { auctionId: number }) => {
  const { auctionId } = props;
  return api
    .url(`${apiPaths.USER_ACTIONS}/favourite/${auctionId}`)
    .post()
    .json()
    .then((res) => {
      return res as { message: string };
    })
    .catch((e) => {
      throw e;
    });
};
